import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  Search, Heart, X, BookOpen, Headphones, Video, Sparkles,
  Waves, ScrollText, GraduationCap, FlaskConical, Library as LibraryIcon,
  Type, Sun, Moon, Minus, Plus, Clock,
} from "lucide-react";
import { ACTIVATIONS, BOOKS, RELEASES, WISDOM, type WisdomPost, type Activation, type Release } from "@/lib/content";
import { RecentlyViewedRail } from "@/components/site/RecentlyViewedRail";
import { absoluteUrl } from "@/lib/site";

// ─── Phase 4 Taxonomy ────────────────────────────────────────────────────

type Category =
  | "Books" | "Music" | "Videos" | "Meditations" | "Affirmations"
  | "Articles" | "Teachings" | "Research" | "Collections";

const CATEGORIES: Category[] = [
  "Books", "Music", "Videos", "Meditations", "Affirmations",
  "Articles", "Teachings", "Research", "Collections",
];

const CATEGORY_SLUG: Record<Category, string> = {
  "Books": "books", "Music": "music", "Videos": "videos",
  "Meditations": "meditations", "Affirmations": "affirmations",
  "Articles": "articles", "Teachings": "teachings",
  "Research": "research", "Collections": "collections",
};

const CATEGORY_ICON: Record<Category, typeof BookOpen> = {
  "Books": BookOpen, "Music": Headphones, "Videos": Video,
  "Meditations": Waves, "Affirmations": Sparkles,
  "Articles": ScrollText, "Teachings": GraduationCap,
  "Research": FlaskConical, "Collections": LibraryIcon,
};

export const Route = createFileRoute("/library/")({
  component: LibraryPage,
  head: () => ({
    meta: [
      { title: "The Consciousness Library · KAfrequency" },
      { name: "description", content: "Every book, song, video, meditation, affirmation, article, teaching, research note, and collection — one searchable, permanent archive." },
      { property: "og:title", content: "The Consciousness Library · KAfrequency" },
      { property: "og:description", content: "The complete searchable KAfrequency canon — books, music, videos, meditations, affirmations, articles, teachings, research, and collections." },
      { property: "og:type", content: "website" },
      { property: "og:url", content: absoluteUrl("/library") },
      { property: "og:site_name", content: "Buddha KA · KAfrequency" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "The Consciousness Library · KAfrequency" },
      { name: "twitter:description", content: "The complete searchable KAfrequency canon." },
    ],
    links: [{ rel: "canonical", href: absoluteUrl("/library") }],
  }),
});

interface LibraryItem {
  id: string;
  kind: "book" | "wisdom" | "activation" | "music" | "video" | "collection";
  slug: string;
  title: string;
  subtitle: string;
  category: Category;
  minutes?: string;
  body?: string[];
  pullQuote?: string;
  searchBlob: string;
  href: string;
  hue: string;
}

// ─── Classifiers ─────────────────────────────────────────────────────────

const RESEARCH_CATS = new Set(["Sacred Systems", "Spiritual Mechanics", "Inner Technology"]);
const ARTICLE_CATS = new Set(["Mirror Thoughts", "Essays", "Artist Notes", "Behind the Music"]);

function classifyWisdom(w: WisdomPost): Category {
  if (RESEARCH_CATS.has(w.category)) return "Research";
  if (ARTICLE_CATS.has(w.category)) return "Articles";
  return "Teachings";
}
function classifyActivation(a: Activation): Category {
  if (/mantra|phrase|affirm/i.test(a.category)) return "Affirmations";
  return "Meditations";
}
function classifyRelease(r: Release): Category {
  return /video|film|visual/i.test(r.type) ? "Videos" : "Music";
}

const HUES = [
  "from-amber-900/50 via-yellow-800/40 to-amber-950/60",
  "from-emerald-900/50 via-emerald-800/40 to-emerald-950/60",
  "from-violet-900/50 via-violet-800/40 to-violet-950/60",
  "from-rose-900/50 via-rose-800/40 to-rose-950/60",
  "from-sky-900/50 via-sky-800/40 to-sky-950/60",
  "from-orange-900/50 via-orange-800/40 to-orange-950/60",
];
const hueFor = (seed: string) => {
  let h = 0; for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return HUES[h % HUES.length];
};

// ─── Curated Collections (permanent portals) ─────────────────────────────

const COLLECTIONS: Array<{ slug: string; title: string; subtitle: string; href: string; blob: string }> = [
  { slug: "collection-music",       title: "The Complete Discography",  subtitle: "Every KAfrequency release, sutra, and frequency transmission.", href: "/music",       blob: "music discography frequencies releases sutras" },
  { slug: "collection-activations", title: "Daily Activations Archive", subtitle: "Rising, midday, evening, and night-seal activations.",         href: "/activations", blob: "activations meditation rising midday evening night seal" },
  { slug: "collection-books",       title: "The Written Canon",         subtitle: "Long-form books and study editions.",                          href: "/books",       blob: "books canon writing long form" },
  { slug: "collection-gates",       title: "The 47 Gates Path",         subtitle: "The consciousness gate sequence and journey map.",             href: "/gates",       blob: "47 gates journey consciousness path map" },
  { slug: "collection-playlists",   title: "Personal Playlists",        subtitle: "Curated cycles you build for your own practice.",              href: "/playlists",   blob: "playlists collections practice" },
  { slug: "collection-codex",       title: "The Codex",                 subtitle: "Concepts, symbols, and inner-technology references.",          href: "/codex",       blob: "codex concepts symbols reference research" },
];

const INDEX: LibraryItem[] = [
  ...BOOKS.map((b) => ({
    id: `book:${b.slug}`, kind: "book" as const, slug: b.slug,
    title: b.title, subtitle: b.subtitle, category: "Books" as Category,
    body: [b.longDescription],
    searchBlob: `${b.title} ${b.subtitle} ${b.longDescription}`.toLowerCase(),
    href: `/books/${b.slug}`, hue: hueFor(b.slug),
  })),
  ...WISDOM.map((w) => ({
    id: `wisdom:${w.slug}`, kind: "wisdom" as const, slug: w.slug,
    title: w.title, subtitle: w.excerpt, category: classifyWisdom(w),
    minutes: w.readingTime, body: w.body, pullQuote: w.pullQuote,
    searchBlob: `${w.title} ${w.excerpt} ${w.pullQuote} ${w.body.join(" ")} ${w.category}`.toLowerCase(),
    href: `/wisdom/${w.slug}`, hue: hueFor(w.slug),
  })),
  ...ACTIVATIONS.map((a) => ({
    id: `activation:${a.slug}`, kind: "activation" as const, slug: a.slug,
    title: a.title, subtitle: a.intention, category: classifyActivation(a),
    minutes: a.duration, body: [a.intention, a.instruction, `Phrase — ${a.phrase}`, `Journal — ${a.journal}`],
    searchBlob: `${a.title} ${a.category} ${a.intention} ${a.instruction} ${a.phrase}`.toLowerCase(),
    href: `/activations/${a.slug}`, hue: hueFor(a.slug),
  })),
  ...RELEASES.map((r) => {
    const cat = classifyRelease(r);
    const kind = cat === "Videos" ? "video" as const : "music" as const;
    return {
      id: `${kind}:${r.slug}`, kind, slug: r.slug,
      title: r.title, subtitle: r.statement ?? r.type, category: cat,
      body: r.statement ? [r.statement] : undefined,
      searchBlob: `${r.title} ${r.type} ${r.frequency} ${r.mood?.join(" ") ?? ""} ${r.statement ?? ""}`.toLowerCase(),
      href: `/music/${r.slug}`, hue: hueFor(r.slug),
    };
  }),
  ...COLLECTIONS.map((c) => ({
    id: c.slug, kind: "collection" as const, slug: c.slug,
    title: c.title, subtitle: c.subtitle, category: "Collections" as Category,
    searchBlob: `${c.title} ${c.subtitle} ${c.blob}`.toLowerCase(),
    href: c.href, hue: hueFor(c.slug),
  })),
];

// ─── Local storage ────────────────────────────────────────────────────────

const FAV_KEY = "ka.library.favorites.v1";
const CONT_KEY = "ka.library.continue.v1";

function useLocalSet(key: string): [Set<string>, (id: string) => void] {
  const [set, setSet] = useState<Set<string>>(new Set());
  useEffect(() => {
    try { const raw = localStorage.getItem(key); if (raw) setSet(new Set(JSON.parse(raw))); } catch { /* noop */ }
  }, [key]);
  const toggle = (id: string) => {
    setSet((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      try { localStorage.setItem(key, JSON.stringify([...next])); } catch { /* noop */ }
      return next;
    });
  };
  return [set, toggle];
}

interface ContinueEntry { id: string; at: number; }
function useContinue(): [ContinueEntry[], (id: string) => void] {
  const [list, setList] = useState<ContinueEntry[]>([]);
  useEffect(() => {
    try { const raw = localStorage.getItem(CONT_KEY); if (raw) setList(JSON.parse(raw)); } catch { /* noop */ }
  }, []);
  const push = (id: string) => {
    setList((prev) => {
      const next = [{ id, at: Date.now() }, ...prev.filter((e) => e.id !== id)].slice(0, 8);
      try { localStorage.setItem(CONT_KEY, JSON.stringify(next)); } catch { /* noop */ }
      return next;
    });
  };
  return [list, push];
}

// ─── Component ────────────────────────────────────────────────────────────

function LibraryPage() {
  const [query, setQuery] = useState("");
  const [activeCat, setActiveCat] = useState<Category | "All" | "Favorites">("All");
  const [reading, setReading] = useState<LibraryItem | null>(null);
  const [favs, toggleFav] = useLocalSet(FAV_KEY);
  const [cont, pushCont] = useContinue();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "/" && document.activeElement?.tagName !== "INPUT") {
        e.preventDefault(); inputRef.current?.focus();
      }
      if (e.key === "Escape" && reading) setReading(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [reading]);

  const q = query.trim().toLowerCase();
  const filtered = useMemo(() => {
    return INDEX.filter((it) => {
      if (activeCat === "Favorites") { if (!favs.has(it.id)) return false; }
      else if (activeCat !== "All" && it.category !== activeCat) return false;
      if (!q) return true;
      return it.searchBlob.includes(q);
    });
  }, [q, activeCat, favs]);

  const grouped = useMemo(() => {
    const g = new Map<Category, LibraryItem[]>();
    for (const cat of CATEGORIES) g.set(cat, []);
    for (const it of filtered) g.get(it.category)!.push(it);
    return new Map([...g.entries()].filter(([, arr]) => arr.length > 0));
  }, [filtered]);

  const continueItems = cont
    .map((e) => INDEX.find((i) => i.id === e.id))
    .filter((x): x is LibraryItem => Boolean(x));

  const recommended = useMemo(() => {
    const last = continueItems[0];
    if (!last) return INDEX.slice(0, 4);
    const sameCat = INDEX.filter((i) => i.category === last.category && i.id !== last.id).slice(0, 3);
    const otherKind = INDEX.filter((i) => i.kind !== last.kind && !sameCat.includes(i)).slice(0, 1);
    return [...sameCat, ...otherKind];
  }, [continueItems]);

  const counts = useMemo(() => {
    const c: Record<string, number> = {};
    for (const it of INDEX) c[it.category] = (c[it.category] ?? 0) + 1;
    return c;
  }, []);

  return (
    <>
      <section className="relative border-b hairline">
        <div className="absolute inset-0 -z-10 halo opacity-40" aria-hidden />
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 sm:py-24">
          <p className="text-xs uppercase tracking-[0.3em] text-gold">The Consciousness Library</p>
          <h1 className="mt-4 font-serif text-5xl sm:text-6xl md:text-7xl">The Library</h1>
          <p className="mt-6 max-w-2xl text-lg text-muted-foreground">
            Every book, song, video, meditation, affirmation, article, teaching, and research note — one searchable, permanent temple of transmission.
          </p>

          <div className="mt-10 flex items-center gap-3 rounded-2xl border hairline bg-obsidian/60 px-5 py-4 backdrop-blur-sm focus-within:border-gold">
            <Search size={18} className="text-gold shrink-0" />
            <input
              ref={inputRef}
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the archive…"
              className="flex-1 bg-transparent text-base text-ivory placeholder:text-muted-foreground focus:outline-none"
              aria-label="Search the library"
            />
            {query && (
              <button onClick={() => setQuery("")} className="text-muted-foreground hover:text-gold" aria-label="Clear">
                <X size={16} />
              </button>
            )}
            <kbd className="hidden sm:inline rounded border hairline px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">/</kbd>
          </div>

          <div className="mt-6 flex flex-wrap gap-2">
            {(["All", "Favorites", ...CATEGORIES] as const).map((c) => {
              const active = activeCat === c;
              const count = c === "All" ? INDEX.length : c === "Favorites" ? favs.size : counts[c] ?? 0;
              return (
                <button
                  key={c}
                  onClick={() => setActiveCat(c)}
                  className={`inline-flex items-center gap-2 rounded-full border px-4 py-1.5 text-xs uppercase tracking-[0.2em] transition-colors min-h-[36px] ${
                    active ? "border-gold bg-gold/10 text-gold-luminous" : "hairline text-muted-foreground hover:border-gold hover:text-gold"
                  }`}
                >
                  {c === "Favorites" ? <Heart size={12} className={active ? "fill-current" : ""} /> : null}
                  {c}
                  <span className="text-[10px] opacity-70">{count}</span>
                </button>
              );
            })}
          </div>

          <nav aria-label="Browse the shelves" className="mt-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-gold">Browse the shelves</p>
            <ul className="mt-3 flex flex-wrap gap-2">
              {CATEGORIES.map((name) => (
                <li key={name}>
                  <Link
                    to="/library/$category"
                    params={{ category: CATEGORY_SLUG[name] }}
                    className="inline-flex items-center rounded-full border hairline px-3 py-1 text-[11px] uppercase tracking-[0.2em] text-muted-foreground hover:border-gold hover:text-gold"
                  >
                    {name}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </section>

      {continueItems.length > 0 && !q && activeCat === "All" && (
        <section className="mx-auto max-w-7xl px-4 pt-12 sm:px-6">
          <div className="mb-4 flex items-center gap-3">
            <Clock size={14} className="text-gold" />
            <h2 className="text-xs uppercase tracking-[0.3em] text-gold">Continue</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {continueItems.slice(0, 4).map((it) => (
              <MiniCard key={it.id} item={it} onOpen={() => openItem(it)} />
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 py-16 sm:px-6">
        {filtered.length === 0 ? (
          <div className="rounded-2xl border hairline bg-obsidian/40 p-16 text-center">
            <p className="font-serif text-2xl text-ivory">Nothing in this shelf yet.</p>
            <p className="mt-2 text-sm text-muted-foreground">Try another search or category.</p>
          </div>
        ) : (
          [...grouped.entries()].map(([cat, items]) => {
            const Icon = CATEGORY_ICON[cat];
            return (
              <div key={cat} className="mb-16 last:mb-0">
                <div className="mb-6 flex items-end justify-between border-b hairline pb-3">
                  <div className="flex items-center gap-3">
                    <Icon size={16} className="text-gold" />
                    <Link to="/library/$category" params={{ category: CATEGORY_SLUG[cat] }} className="font-serif text-2xl sm:text-3xl hover:text-gold-luminous">{cat}</Link>
                    <span className="text-xs text-muted-foreground">{items.length}</span>
                  </div>
                </div>
                <Shelf items={items} onOpen={openItem} favs={favs} toggleFav={toggleFav} />
              </div>
            );
          })
        )}
      </section>

      {!q && recommended.length > 0 && (
        <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6">
          <div className="mb-4 flex items-center gap-3">
            <Sparkles size={14} className="text-gold" />
            <h2 className="text-xs uppercase tracking-[0.3em] text-gold">Recommended next</h2>
          </div>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {recommended.map((it) => (
              <MiniCard key={it.id} item={it} onOpen={() => openItem(it)} />
            ))}
          </div>
        </section>
      )}

      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6">
        <RecentlyViewedRail />
      </section>

      {reading && (
        <ReadingMode
          item={reading}
          onClose={() => setReading(null)}
          favorited={favs.has(reading.id)}
          onFav={() => toggleFav(reading.id)}
        />
      )}
    </>
  );

  function openItem(it: LibraryItem) {
    pushCont(it.id);
    if (it.body && it.body.length > 0 && it.kind !== "collection") setReading(it);
    else window.location.href = it.href;
  }
}

// ─── Shelf ────────────────────────────────────────────────────────────────

function Shelf({
  items, onOpen, favs, toggleFav,
}: {
  items: LibraryItem[];
  onOpen: (it: LibraryItem) => void;
  favs: Set<string>;
  toggleFav: (id: string) => void;
}) {
  return (
    <div className="relative">
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {items.map((it) => (
          <Spine key={it.id} item={it} onOpen={() => onOpen(it)} favorited={favs.has(it.id)} onFav={() => toggleFav(it.id)} />
        ))}
      </div>
      <div className="mt-4 h-[2px] w-full bg-gradient-to-r from-transparent via-gold/40 to-transparent" />
      <div className="h-3 w-full bg-gradient-to-b from-black/60 to-transparent rounded-b-xl" />
    </div>
  );
}

function Spine({ item, onOpen, favorited, onFav }: { item: LibraryItem; onOpen: () => void; favorited: boolean; onFav: () => void; }) {
  return (
    <div className="group relative">
      <button
        onClick={onOpen}
        className={`relative flex h-56 w-full flex-col justify-between overflow-hidden rounded-xl border hairline bg-gradient-to-br ${item.hue} p-5 text-left transition-all hover:-translate-y-1 hover:border-gold hover:shadow-gold focus:outline-none focus:ring-2 focus:ring-gold`}
      >
        <div className="absolute inset-0 opacity-30 mix-blend-overlay pointer-events-none"
          style={{ backgroundImage: "radial-gradient(circle at 30% 20%, rgba(212,175,55,0.4), transparent 60%)" }} />
        <div className="relative">
          <p className="text-[10px] uppercase tracking-[0.3em] text-gold-luminous">{item.category}</p>
          <h2 className="mt-3 font-serif text-xl leading-tight text-ivory line-clamp-3">{item.title}</h2>
        </div>
        <div className="relative">
          <p className="line-clamp-2 text-xs text-ivory/70">{item.subtitle}</p>
          {item.minutes && <p className="mt-2 text-[10px] uppercase tracking-widest text-gold/80">{item.minutes}</p>}
        </div>
      </button>
      {item.kind !== "collection" && (
        <button
          onClick={(e) => { e.stopPropagation(); onFav(); }}
          aria-pressed={favorited}
          aria-label={favorited ? "Unsave" : "Save"}
          className={`absolute right-3 top-3 rounded-full border p-2 backdrop-blur-sm transition-colors ${
            favorited ? "border-gold bg-obsidian/70 text-gold-luminous" : "hairline bg-obsidian/50 text-ivory/60 hover:text-gold hover:border-gold"
          }`}
        >
          <Heart size={12} className={favorited ? "fill-current" : ""} />
        </button>
      )}
    </div>
  );
}

function MiniCard({ item, onOpen }: { item: LibraryItem; onOpen: () => void }) {
  return (
    <button
      onClick={onOpen}
      className={`group flex h-32 w-full flex-col justify-between rounded-xl border hairline bg-gradient-to-br ${item.hue} p-4 text-left transition-all hover:border-gold hover:-translate-y-0.5`}
    >
      <p className="text-[10px] uppercase tracking-[0.3em] text-gold-luminous">{item.category}</p>
      <h2 className="font-serif text-base text-ivory line-clamp-2">{item.title}</h2>
    </button>
  );
}

// ─── Reading Mode ─────────────────────────────────────────────────────────

function ReadingMode({
  item, onClose, favorited, onFav,
}: { item: LibraryItem; onClose: () => void; favorited: boolean; onFav: () => void }) {
  const [size, setSize] = useState(18);
  const [dark, setDark] = useState(true);
  const [serif, setSerif] = useState(true);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  return (
    <div className={`fixed inset-0 z-[100] overflow-y-auto ${dark ? "bg-[#0b0a08] text-ivory" : "bg-[#f6f1e6] text-[#1a1712]"}`}>
      <div className={`sticky top-0 z-10 flex items-center justify-between border-b px-4 py-3 backdrop-blur-md sm:px-8 ${dark ? "border-white/10 bg-[#0b0a08]/80" : "border-black/10 bg-[#f6f1e6]/80"}`}>
        <button onClick={onClose} className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] opacity-80 hover:opacity-100">
          <X size={14} /> Close
        </button>
        <div className="flex items-center gap-2">
          <ToolBtn onClick={() => setSize((s) => Math.max(14, s - 1))} label="Smaller"><Minus size={14} /></ToolBtn>
          <span className="text-xs opacity-70 w-6 text-center">{size}</span>
          <ToolBtn onClick={() => setSize((s) => Math.min(28, s + 1))} label="Larger"><Plus size={14} /></ToolBtn>
          <ToolBtn onClick={() => setSerif((s) => !s)} label="Toggle typeface"><Type size={14} /></ToolBtn>
          <ToolBtn onClick={() => setDark((d) => !d)} label="Toggle theme">{dark ? <Sun size={14} /> : <Moon size={14} />}</ToolBtn>
          <ToolBtn onClick={onFav} label="Save">
            <Heart size={14} className={favorited ? "fill-current text-gold-luminous" : ""} />
          </ToolBtn>
        </div>
      </div>

      <article
        className="mx-auto max-w-2xl px-6 py-16 sm:py-24"
        style={{ fontSize: size, lineHeight: 1.7, fontFamily: serif ? "var(--font-serif, ui-serif)" : "var(--font-sans, ui-sans-serif)" }}
      >
        <p className="text-xs uppercase tracking-[0.3em] text-gold">{item.category}{item.minutes ? ` · ${item.minutes}` : ""}</p>
        <h1 className={`mt-4 font-serif leading-tight ${dark ? "text-ivory" : "text-[#1a1712]"}`} style={{ fontSize: size * 2.2 }}>
          {item.title}
        </h1>
        {item.pullQuote && (
          <blockquote className="my-10 border-l-2 border-gold pl-6 font-serif italic text-gold-luminous" style={{ fontSize: size * 1.25 }}>
            &ldquo;{item.pullQuote}&rdquo;
          </blockquote>
        )}
        <div className="mt-8 space-y-6">
          {(item.body ?? []).map((p, i) => <p key={i}>{p}</p>)}
        </div>
        <div className={`mt-16 border-t pt-8 ${dark ? "border-white/10" : "border-black/10"}`}>
          <a
            href={item.href}
            className="inline-flex rounded-full bg-gold-gradient px-6 py-3 text-sm font-semibold text-obsidian shadow-gold"
          >
            Open full page →
          </a>
        </div>
      </article>
    </div>
  );
}

function ToolBtn({ children, onClick, label }: { children: React.ReactNode; onClick: () => void; label: string }) {
  return (
    <button
      onClick={onClick}
      aria-label={label}
      className="inline-flex h-9 w-9 items-center justify-center rounded-full border hairline hover:border-gold hover:text-gold"
    >
      {children}
    </button>
  );
}
